import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { agentsApi } from "@/features/agents/api";
import { assistantsApi } from "@/features/assistants/api";
import {
  whatsappApi,
  type WhatsAppConfig,
  type WhatsAppConfigPayload,
} from "@/features/telephony/api";

const KEY = ["whatsapp-configs"] as const;

const WEBHOOK_PATH = "/whatsapp/webhook/";

type FormState = {
  phone_number_id: string;
  display_number: string;
  // "agent:<id>" or "assistant:<id>" — exactly one answers the number.
  answered_by: string;
  verify_token: string;
  whatsapp_token: string;
  app_secret: string;
  is_active: boolean;
};

const EMPTY: FormState = {
  phone_number_id: "",
  display_number: "",
  answered_by: "",
  verify_token: "",
  whatsapp_token: "",
  app_secret: "",
  is_active: true,
};

export default function WhatsAppPage() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WhatsAppConfig | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppConfig | null>(null);

  const listQ = useQuery({ queryKey: KEY, queryFn: whatsappApi.list });
  const agentsQ = useQuery({ queryKey: ["agents", "all"], queryFn: agentsApi.list });
  const agents = agentsQ.data ?? [];
  const assistantsQ = useQuery({
    queryKey: ["assistants"],
    queryFn: assistantsApi.list,
  });
  const assistants = assistantsQ.data ?? [];

  const webhookUrl = useMemo(
    () => `${window.location.origin}${WEBHOOK_PATH}`,
    [],
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      const [kind, id] = form.answered_by.split(":");
      const payload: WhatsAppConfigPayload = {
        phone_number_id: form.phone_number_id.trim(),
        display_number: form.display_number.trim(),
        agent: kind === "agent" ? id : null,
        assistant: kind === "assistant" ? id : null,
        verify_token: form.verify_token.trim(),
        is_active: form.is_active,
      };
      // Only send secrets when the user actually typed one.
      if (form.whatsapp_token) payload.whatsapp_token = form.whatsapp_token;
      if (form.app_secret) payload.app_secret = form.app_secret;
      return editing
        ? whatsappApi.update(editing.id, payload)
        : whatsappApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      setDialogOpen(false);
      toast.success(editing ? "WhatsApp number updated" : "WhatsApp number added");
    },
    onError: () => toast.error("Couldn't save — check the fields and try again."),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => whatsappApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      setDeleteTarget(null);
      toast.success("WhatsApp number removed");
    },
    onError: () => toast.error("Couldn't remove that number."),
  });

  function openAdd() {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  }

  function openEdit(cfg: WhatsAppConfig) {
    setEditing(cfg);
    setForm({
      phone_number_id: cfg.phone_number_id,
      display_number: cfg.display_number,
      answered_by: cfg.assistant
        ? `assistant:${cfg.assistant}`
        : cfg.agent
        ? `agent:${cfg.agent}`
        : "",
      verify_token: cfg.verify_token,
      whatsapp_token: "",
      app_secret: "",
      is_active: cfg.is_active,
    });
    setDialogOpen(true);
  }

  const configs = listQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">WhatsApp</h1>
          <p className="text-sm text-muted-foreground">
            Let WhatsApp users call an agent directly — native voice over Meta's
            Cloud API, no phone line or SIP.
          </p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="mr-1.5 size-4" /> Add WhatsApp number
        </Button>
      </div>

      {/* Webhook setup hint */}
      <Card>
        <CardContent className="space-y-1 py-4 text-sm">
          <p className="font-medium">Meta webhook setup</p>
          <p className="text-muted-foreground">
            In Meta → your app → WhatsApp → Configuration, set the Callback URL to{" "}
            <code className="rounded bg-muted px-1.5 py-0.5">{webhookUrl}</code>{" "}
            and the Verify token to the one you enter below. Subscribe to the{" "}
            <code className="rounded bg-muted px-1.5 py-0.5">calls</code> field.
          </p>
        </CardContent>
      </Card>

      {listQ.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            No WhatsApp numbers yet. Add one to start answering WhatsApp calls.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {configs.map((cfg) => (
            <Card key={cfg.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-3">
                  <MessageCircle className="size-5 text-emerald-600" />
                  <div>
                    <div className="font-medium">
                      {cfg.display_number || cfg.phone_number_id}
                      {!cfg.is_active && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {cfg.assistant_name
                        ? `Answered by ${cfg.assistant_name} (Assistant)`
                        : cfg.agent_name
                        ? `Answered by ${cfg.agent_name} (Agent)`
                        : "No one assigned"}
                      {" · "}
                      {cfg.token_set ? "token ✓" : "no token"}
                      {" · "}
                      {cfg.secret_set ? "secret ✓" : "no secret"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(cfg)}>
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTarget(cfg)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit WhatsApp number" : "Add WhatsApp number"}
            </DialogTitle>
            <DialogDescription>
              From Meta → WhatsApp → API Setup. Secrets are stored encrypted; leave
              them blank when editing to keep the existing value.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <Field label="Phone number ID">
              <Input
                value={form.phone_number_id}
                onChange={(e) =>
                  setForm({ ...form, phone_number_id: e.target.value })
                }
                placeholder="e.g. 123456789012345"
              />
            </Field>
            <Field label="Display number (optional)">
              <Input
                value={form.display_number}
                onChange={(e) =>
                  setForm({ ...form, display_number: e.target.value })
                }
                placeholder="+14155551234"
              />
            </Field>
            <Field label="Answered by">
              <Select
                value={form.answered_by || undefined}
                onValueChange={(v) => setForm({ ...form, answered_by: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select who answers this number" />
                </SelectTrigger>
                <SelectContent>
                  {assistants.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Assistants</SelectLabel>
                      {assistants.map((a) => (
                        <SelectItem key={a.id} value={`assistant:${a.id}`}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {agents.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Agents</SelectLabel>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={`agent:${a.id}`}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Handles both voice calls and text messages on this number.
              </p>
            </Field>
            <Field label="Verify token">
              <Input
                value={form.verify_token}
                onChange={(e) =>
                  setForm({ ...form, verify_token: e.target.value })
                }
                placeholder="A string you also set in Meta's webhook config"
                autoComplete="off"
                name="wa-verify-token"
              />
            </Field>
            <Field
              label={
                editing && editing.token_set
                  ? "Access token (set — leave blank to keep)"
                  : "Access token"
              }
            >
              <Input
                type="password"
                value={form.whatsapp_token}
                onChange={(e) =>
                  setForm({ ...form, whatsapp_token: e.target.value })
                }
                placeholder="WhatsApp Cloud API access token"
                autoComplete="new-password"
                name="wa-access-token"
              />
            </Field>
            <Field
              label={
                editing && editing.secret_set
                  ? "App secret (set — leave blank to keep)"
                  : "App secret"
              }
            >
              <Input
                type="password"
                value={form.app_secret}
                onChange={(e) =>
                  setForm({ ...form, app_secret: e.target.value })
                }
                placeholder="Meta App secret (webhook signature)"
                autoComplete="new-password"
                name="wa-app-secret"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              Active (answer calls to this number)
            </label>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMut.mutate()}
              disabled={
                !form.phone_number_id.trim() ||
                !form.answered_by ||
                saveMut.isPending
              }
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove WhatsApp number?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.display_number || deleteTarget?.phone_number_id} will
              stop answering WhatsApp calls. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
